import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos, categories } from '@/mocks/videos';
import type { VideoItem } from '@/mocks/videos';

export default function Home() {
  const [activeTab, setActiveTab] = useState('hot');

  const sortedByHot = [...videos].sort((a, b) => b.hotScore - a.hotScore);
  const sortedByNew = [...videos].sort(
    (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );

  const displayVideos: VideoItem[] = activeTab === 'hot' ? sortedByHot : sortedByNew;
  const featuredVideo = sortedByHot[0];
  const sideVideos = sortedByHot.slice(1, 4);
  const gridVideos = activeTab === 'hot'
    ? sortedByHot.slice(4, 10)
    : sortedByNew.slice(0, 6);
  const compactVideos = activeTab === 'hot'
    ? sortedByHot.slice(10)
    : sortedByNew.slice(6);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '吃瓜视频',
    alternateName: '吃瓜视频平台',
    url: 'https://readyue.cn',
    description: '吃瓜视频平台汇聚最新最热娱乐八卦、明星吃瓜、社会热点视频内容，每日实时更新。海量吃瓜视频一站式观看，不错过任何热门瓜事。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://readyue.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '吃瓜视频',
    url: 'https://readyue.cn',
    description: '最新最热娱乐八卦视频平台',
    sameAs: [
      'https://weibo.com/chiguavideo',
      'https://www.douyin.com/user/chiguavideo',
    ],
  };

  return (
    <>
      <Navbar />
      <ScrollToTop />
      <main>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>

        <header className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20dramatic%20cinematic%20scene%20with%20vibrant%20warm%20coral%20and%20amber%20gradient%20background%2C%20abstract%20flowing%20shapes%20representing%20entertainment%20news%20and%20social%20media%20buzz%2C%20modern%20minimal%20design%2C%20clean%20aesthetic%2C%20digital%20art%20style%20with%20soft%20glowing%20particles%2C%20trending%20topic%20vibe%2C%20no%20text&width=1920&height=600&seq=hero-chigua-2026&orientation=landscape"
            alt="吃瓜视频 - 最新最热娱乐八卦视频平台"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-foreground-950/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              <span className="text-primary-400">吃</span><span className="text-background-50">瓜</span><span className="text-accent-400">视</span><span className="text-background-50">频</span>
            </h1>
            <p className="text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto mb-6 leading-relaxed">
              最新最热娱乐八卦、明星吃瓜、社会热点视频内容，每日实时更新。海量吃瓜视频一站式观看，不错过任何热门瓜事，来这里吃最新鲜的瓜！
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                立即吃瓜
              </Link>
              <Link
                to="/about"
                className="px-6 py-2.5 rounded-full border border-background-50/40 text-background-50 text-sm md:text-base font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </header>

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-5 rounded-full bg-primary-500"></div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground-900">热门分类</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {categories.filter(c => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200/50 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </div>
                  <span className="text-xs md:text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                  <span className="text-[10px] md:text-xs text-foreground-500">{cat.count}个视频</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100/50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground-900">精选推荐</h2>
              </div>
              <div className="flex items-center bg-background-200/70 rounded-full p-1">
                <button
                  onClick={() => setActiveTab('hot')}
                  className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeTab === 'hot'
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  热门排行
                </button>
                <button
                  onClick={() => setActiveTab('new')}
                  className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeTab === 'new'
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  最新发布
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
              <div className="lg:col-span-2">
                <VideoCard video={featuredVideo} variant="featured" />
              </div>
              <div className="flex flex-col gap-3">
                {sideVideos.map((v) => (
                  <VideoCard key={v.id} video={v} variant="compact" />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-8">
              {gridVideos.map((v) => (
                <VideoCard key={v.id} video={v} variant="default" />
              ))}
            </div>

            {compactVideos.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {compactVideos.map((v) => (
                  <VideoCard key={v.id} video={v} variant="compact" />
                ))}
              </div>
            )}

            <div className="text-center mt-8">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部视频
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-1 h-5 rounded-full bg-accent-500"></div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground-900">为什么选择吃瓜视频</h2>
              </div>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">吃瓜视频致力于为广大群众提供最新鲜、最全面的吃瓜体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-flashlight-line', title: '实时更新', desc: '全天候追踪热点，第一时间推送最新瓜事' },
                { icon: 'ri-stack-line', title: '海量内容', desc: '涵盖明星、八卦、社会热点等多种分类' },
                { icon: 'ri-shield-check-line', title: '真实可靠', desc: '严格审核内容来源，确保信息准确可信' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '手机、平板、电脑全终端完美适配' },
              ].map((item) => (
                <div key={item.title} className="p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-accent-200/50 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 mb-3">
              <div className="w-1 h-5 rounded-full bg-primary-500"></div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground-900">吃瓜不停歇</h2>
            </div>
            <p className="text-sm text-foreground-500 mb-6 max-w-lg mx-auto">
              每天都有新鲜热辣的瓜事等你来吃，快加入我们一起快乐吃瓜吧！
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即开始吃瓜
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}